import React, { useCallback, useMemo, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  createFilterOptions,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import EditIcon from '@mui/icons-material/Edit';
import classNames from 'classnames';
import CircularProgress from '@mui/material/CircularProgress';
import { useWatch } from 'react-hook-form';
import { useLabels } from 'modules/common/hooks';
import { useNoteContext } from 'modules/notes/NoteContext';
import { CustomLabel as Label } from 'modules/timesheet-labels/labelSchema';
import { useMessages } from 'modules/intl';
import { LabelType } from 'graphql/types';
import { useCreateCustomLabel } from '../hooks';
import { EditKeyDialog } from './EditKeyDialog';

const useRenderStyles = makeStyles((theme) => ({
  addLi: {
    display: 'flex',
    padding: '0px !important',
    color: theme.palette.primary.main
  },
  add: {
    margin: theme.spacing(0),
    padding: theme.spacing(1.5),
    display: 'block',
    justifyContent: 'flex-start'
  },
  edit: {
    display: 'flex',
    visibility: 'hidden'
  },
  li: {
    display: 'flex',
    '&.MuiAutocomplete-option': {
      justifyContent: 'space-between'
    },
    '&:hover $edit': {
      visibility: 'visible'
    }
  },
  input: {
    padding: `${theme.spacing(0.313, 4, 0.313, 0.75)} !important`
  }
}));

type CustomLabelKeyPickerProps = {
  className?: string;
  value?: CustomLabel;
  setLabel: (label?: CustomLabel, resetValue?: boolean) => void;
  fieldName: string;
};

const loadingText = (
  <Typography>
    <FormattedMessage id="customLabels.loading" />
  </Typography>
);

const ADD_CUSTOM_LABEL = 'ADD_CUSTOM_LABEL';

const popUpWidth = { width: 300 };

type CustomLabel = Label & { inputValue?: string };

const messagesKeys = [
  'customLabels.key',
  'customLabels.typeToAddKey',
  'customLabels.keyPickerPlaceholder'
];

const isOptionEqualToValue = (option: CustomLabel, value: CustomLabel) =>
  option.id === value.id;

export const CustomLabelKeyPicker: React.FC<CustomLabelKeyPickerProps> = ({
  value,
  setLabel,
  fieldName
}: CustomLabelKeyPickerProps) => {
  const classesRender = useRenderStyles();
  const intl = useIntl();

  const customLabelfieldValues = useWatch({
    name: `${fieldName}`
  });

  const customLabelsIdSet = useMemo(
    () =>
      customLabelfieldValues?.reduce((acc: Set<string>, item: CustomLabel) => {
        acc.add(item?.id);
        return acc;
      }, new Set()),
    [customLabelfieldValues]
  );
  const messages = useMessages(messagesKeys);
  const { date } = useNoteContext();
  const { loading, customLabels } = useLabels(date);
  const [editLabel, setEditLabel] = useState<CustomLabel | null>(null);
  const initialValue = useRef(value);

  const {
    loading: creatingCustomLabel,
    createCustomLabel
  } = useCreateCustomLabel();

  const onEditClose = useCallback(() => {
    setEditLabel(null);
  }, []);

  const handleOnDeleteClose = useCallback(
    (id?: string) => {
      if (id === value?.id) {
        setLabel(undefined, true);
      }
      onEditClose();
    },
    [onEditClose, setLabel, value?.id]
  );

  const handleOnEditClose = useCallback(
    (updatedLabel?: CustomLabel) => {
      if (updatedLabel && updatedLabel.id === value?.id) {
        setLabel(updatedLabel, false);
      }
      onEditClose();
    },
    [onEditClose, setLabel, value?.id]
  );

  const handleEditKey = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, label) => {
      event.preventDefault();
      event.stopPropagation();
      setEditLabel(label);
    },
    []
  );

  const handleCreateCustomLabelOption = useCallback(
    async (label: CustomLabel) => {
      if (label.inputValue) {
        const newLabel = await createCustomLabel(label.inputValue!);
        setLabel(newLabel);
      }
    },
    [createCustomLabel, setLabel]
  );

  const handleChange = useCallback(
    (_, label) => {
      if (label && label.id === ADD_CUSTOM_LABEL) {
        handleCreateCustomLabelOption(label);
      } else {
        setLabel(label);
      }
    },
    [handleCreateCustomLabelOption, setLabel]
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option) => {
      if (option?.id === ADD_CUSTOM_LABEL) {
        return (
          <li
            {...props}
            className={classNames(props.className, classesRender.addLi)}
            data-testid="add-custom-label-key-option-button"
          >
            <Tooltip title={option.name} disableInteractive>
              <Typography
                variant="inherit"
                noWrap
                className={classesRender.add}
              >
                {option.name}
              </Typography>
            </Tooltip>
          </li>
        );
      }

      return (
        <li
          {...props}
          className={classNames(classesRender.li, props.className)}
          data-testid={`li-${option.id}`}
        >
          <Tooltip followCursor={true} title={option.name} disableInteractive>
            <Typography variant="inherit" noWrap>
              {option.name}
            </Typography>
          </Tooltip>
          <div className={classesRender.edit}>
            <IconButton
              data-testid="custom-label-edit-button"
              onClick={(event) => handleEditKey(event, option)}
            >
              <EditIcon />
            </IconButton>
          </div>
        </li>
      );
    },
    [classesRender, handleEditKey]
  );
  const getOptionDisabled = useCallback(
    (option) =>
      customLabelsIdSet.has(option.id) &&
      initialValue.current?.id !== option.id,
    [customLabelsIdSet]
  );
  const filterOptions = useCallback(
    (options: CustomLabel[], state) => {
      const defaultFilterOptions = createFilterOptions<CustomLabel>();
      const filteredOptions = defaultFilterOptions(options, state);
      const { inputValue: value } = state;
      const inputValue = value?.trim();
      if (
        !filteredOptions.length ||
        (inputValue &&
          !filteredOptions.find(
            (o) => o.name.toLocaleLowerCase() === inputValue.toLocaleLowerCase()
          ))
      ) {
        const name = intl.formatMessage(
          { id: 'customLabels.addCustomLabelKey' },
          { option: inputValue }
        );
        filteredOptions.push({
          id: ADD_CUSTOM_LABEL,
          name: inputValue ? name : messages['customLabels.typeToAddKey'],
          type: LabelType.Textselect,
          inputValue
        });
      }
      return filteredOptions;
    },
    [intl, messages]
  );

  const getOptionLabel = useCallback((option: CustomLabel) => {
    if (option.id === ADD_CUSTOM_LABEL) {
      return option.inputValue ?? '';
    }
    return option.name ?? '';
  }, []);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        label={messages['customLabels.key']}
        placeholder={messages['customLabels.keyPickerPlaceholder']}
        size="small"
        inputProps={{
          ...params.inputProps,
          className: classNames(
            classesRender.input,
            params.inputProps.className
          ),
          'data-testid': 'custom-label-key-input'
        }}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {creatingCustomLabel ? (
                <CircularProgress
                  color="inherit"
                  size={20}
                  data-testid="creating-key-loader"
                />
              ) : null}
              {params.InputProps.endAdornment}
            </>
          )
        }}
      />
    ),
    [classesRender.input, creatingCustomLabel, messages]
  );

  return (
    <>
      <Autocomplete<CustomLabel>
        data-testid="custom-label-key-search"
        key={value?.name}
        value={value}
        loadingText={loadingText}
        loading={loading}
        options={customLabels}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        filterOptions={filterOptions}
        sx={popUpWidth}
        renderInput={renderInput}
        getOptionDisabled={getOptionDisabled}
        isOptionEqualToValue={isOptionEqualToValue}
      />
      {editLabel && (
        <EditKeyDialog
          open
          onClose={onEditClose}
          onEditComplete={handleOnEditClose}
          onDeleteComplete={handleOnDeleteClose}
          label={editLabel}
        />
      )}
    </>
  );
};
